/**
 * useListState — manages all list-page state:
 *   page, perPage, search, sortBy, sortOrder, filters
 *
 * Debounces search input (300ms) and resets page to 1
 * on any filter, search, or sort change.
 *
 * Usage:
 *   const listState = useListState({ defaultPerPage: 6, defaultSortBy: 'name' })
 *   const { data } = useOrganizations(listState.queryParams)
 */
import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import type { PagedRequest } from '@/shared/types'

export interface UseListStateOptions {
    defaultPerPage?: number
    defaultSortBy?: string
    defaultSortOrder?: 'asc' | 'desc'
}

export interface ListState {
    /** Current page number (1-based) */
    page: number
    setPage: (page: number) => void
    /** Items per page */
    perPage: number
    setPerPage: (perPage: number) => void
    /** Raw search input (user types here) */
    searchInput: string
    setSearchInput: (value: string) => void
    /** Debounced search value (used in API calls) */
    search: string
    /** Current sort field */
    sortBy: string | undefined
    setSortBy: (field: string) => void
    /** Current sort direction */
    sortOrder: 'asc' | 'desc'
    setSortOrder: (order: 'asc' | 'desc') => void
    /** Toggle sort direction */
    toggleSortOrder: () => void
    /** Active filters (key → value or value[]) */
    filters: Record<string, string | string[]>
    /** Set a single filter value (resets page to 1) */
    setFilter: (key: string, value: string | string[] | undefined) => void
    /** Clear all filters */
    clearFilters: () => void
    /** Ready-to-use PagedRequest for API calls */
    queryParams: PagedRequest
    /** Reset everything to defaults */
    reset: () => void
}

function useDebounce<T>(value: T, delayMs: number): T {
    const [debounced, setDebounced] = useState(value)
    const timerRef = useRef<ReturnType<typeof setTimeout>>()

    useEffect(() => {
        timerRef.current = setTimeout(() => setDebounced(value), delayMs)
        return () => clearTimeout(timerRef.current)
    }, [value, delayMs])

    return debounced
}

export function useListState(options: UseListStateOptions = {}): ListState {
    const {
        defaultPerPage = 10,
        defaultSortBy,
        defaultSortOrder = 'desc',
    } = options

    const [page, setPageRaw] = useState(1)
    const [perPage, setPerPageRaw] = useState(defaultPerPage)
    const [searchInput, setSearchInputRaw] = useState('')
    const [sortBy, setSortByRaw] = useState<string | undefined>(defaultSortBy)
    const [sortOrder, setSortOrderRaw] = useState<'asc' | 'desc'>(defaultSortOrder)
    const [filters, setFilters] = useState<Record<string, string | string[]>>({})

    const debouncedSearch = useDebounce(searchInput, 300)

    // Wrappers that reset page to 1 on change
    const setPage = useCallback((p: number) => setPageRaw(p), [])

    const setPerPage = useCallback((pp: number) => {
        setPerPageRaw(pp)
        setPageRaw(1)
    }, [])

    const setSearchInput = useCallback((value: string) => {
        setSearchInputRaw(value)
        setPageRaw(1)
    }, [])

    const setSortBy = useCallback((field: string) => {
        setSortByRaw(field)
        setPageRaw(1)
    }, [])

    const setSortOrder = useCallback((order: 'asc' | 'desc') => {
        setSortOrderRaw(order)
        setPageRaw(1)
    }, [])

    const toggleSortOrder = useCallback(() => {
        setSortOrderRaw(prev => prev === 'asc' ? 'desc' : 'asc')
        setPageRaw(1)
    }, [])

    const setFilter = useCallback((key: string, value: string | string[] | undefined) => {
        setFilters(prev => {
            if (value === undefined || (Array.isArray(value) && value.length === 0)) {
                const next = { ...prev }
                delete next[key]
                return next
            }
            return { ...prev, [key]: value }
        })
        setPageRaw(1)
    }, [])

    const clearFilters = useCallback(() => {
        setFilters({})
        setPageRaw(1)
    }, [])

    const reset = useCallback(() => {
        setPageRaw(1)
        setPerPageRaw(defaultPerPage)
        setSearchInputRaw('')
        setSortByRaw(defaultSortBy)
        setSortOrderRaw(defaultSortOrder)
        setFilters({})
    }, [defaultPerPage, defaultSortBy, defaultSortOrder])

    const queryParams: PagedRequest = useMemo(() => {
        const params: PagedRequest = {
            page,
            per_page: perPage,
        }
        if (debouncedSearch) params.search = debouncedSearch
        if (sortBy) params.sort_by = sortBy
        if (sortOrder) params.sort_order = sortOrder
        if (Object.keys(filters).length > 0) params.filters = filters
        return params
    }, [page, perPage, debouncedSearch, sortBy, sortOrder, filters])

    return {
        page,
        setPage,
        perPage,
        setPerPage,
        searchInput,
        setSearchInput,
        search: debouncedSearch,
        sortBy,
        setSortBy,
        sortOrder,
        setSortOrder,
        toggleSortOrder,
        filters,
        setFilter,
        clearFilters,
        queryParams,
        reset,
    }
}
